import { Capacitor } from '@capacitor/core';
import { createRouter, createWebHistory } from '@ionic/vue-router';
import { isPlatform } from '@ionic/vue';
import type { RouteRecordRaw } from 'vue-router';
import TabsLayout from '@/views/TabsLayout.vue';
import { applyAuthMiddleware } from './middleware';

function isInstalledAppRuntime(): boolean {
  if (Capacitor.isNativePlatform() || isPlatform('capacitor')) {
    return true;
  }

  if (typeof navigator !== 'undefined') {
    const ua = navigator.userAgent.toLowerCase();
    return /webview|wv|capacitor/i.test(ua) || /\b; wv\b/i.test(ua);
  }

  return false;
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: () => (isInstalledAppRuntime() ? '/login' : '/landing'),
  },
  { path: '/landing', component: () => import('@/views/LandingView.vue'), meta: { webOnly: true } },
  { path: '/login', component: () => import('@/views/LoginView.vue') },
  { path: '/auth/callback', component: () => import('@/views/AuthCallbackView.vue') },
  {
    path: '/tabs',
    component: TabsLayout,
    children: [
      { path: '', redirect: '/tabs/swipe' },
      { path: 'swipe', component: () => import('@/views/SwipeView.vue') },
      { path: 'inventory', component: () => import('@/views/InventoryView.vue') },
      { path: 'wishlist', component: () => import('@/views/WishlistView.vue') },
      { path: 'matches', component: () => import('@/views/MatchesView.vue') },
      { path: 'notifications', component: () => import('@/views/NotificationsView.vue') },
      { path: 'matches/history', component: () => import('@/views/HistoryTradesView.vue') },
      { path: 'matches/:id', component: () => import('@/views/ChatView.vue') },
      { path: 'profile', component: () => import('@/views/ProfileView.vue') },
      { path: 'catalog', component: () => import('@/views/CatalogView.vue') },
      { path: 'settings', component: () => import('@/views/SettingsView.vue') },
      { path: 'settings/help', component: () => import('@/views/HelpView.vue') },
      { path: 'settings/support', component: () => import('@/views/SupportView.vue') },
      { path: 'settings/subscription', component: () => import('@/views/SubscriptionView.vue') },
      { path: 'three-way', component: () => import('@/views/ThreeWayTradeView.vue') },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

applyAuthMiddleware(router);

router.beforeEach((to) => {
  if (!isInstalledAppRuntime()) return true;

  if (to.path === '/landing' || to.meta.webOnly) {
    return '/login';
  }

  return true;
});

// After each navigation, schedule a lightweight check that clears any
// pointer-events: none left on ion-router-outlet by an interrupted transition.
// Run twice (400 ms + 900 ms) to cover both fast and slow Android WebView
// animation timings; the second pass is a belt-and-suspenders safety net.
function runIonRecovery() {
  if (typeof document === 'undefined') return;
  for (const outlet of Array.from(document.querySelectorAll('ion-router-outlet')) as HTMLElement[]) {
    if (outlet.style.pointerEvents === 'none') outlet.style.pointerEvents = '';

    // Mobile/WebView safeguard: interrupted transitions can leave the current
    // routed page flagged as hidden (`ion-page-hidden` / `ion-page-invisible`),
    // resulting in a white screen even though the DOM is mounted.
    const pages = Array.from(outlet.querySelectorAll('.ion-page')) as HTMLElement[];
    if (!pages.length) continue;

    const visibleCandidate = [...pages].reverse().find((page) =>
      !page.classList.contains('ion-page-hidden') && !page.classList.contains('ion-page-invisible')
    );
    const activePage = visibleCandidate ?? pages[pages.length - 1];
    if (!activePage) continue;

    activePage.classList.remove('ion-page-hidden', 'ion-page-invisible');
    if (activePage.style.display === 'none') activePage.style.display = '';
    if (activePage.style.visibility === 'hidden') activePage.style.visibility = '';
    activePage.removeAttribute('aria-hidden');
  }
}

let _recoveryTimer1: ReturnType<typeof setTimeout> | null = null;
let _recoveryTimer2: ReturnType<typeof setTimeout> | null = null;
router.afterEach(() => {
  if (_recoveryTimer1) clearTimeout(_recoveryTimer1);
  if (_recoveryTimer2) clearTimeout(_recoveryTimer2);
  _recoveryTimer1 = setTimeout(() => { _recoveryTimer1 = null; runIonRecovery(); }, 400);
  _recoveryTimer2 = setTimeout(() => { _recoveryTimer2 = null; runIonRecovery(); }, 900);
});

export default router;
